import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, TrendingUp, DollarSign } from "lucide-react";

interface ROICalculatorProps {
  open: boolean;
  onClose: () => void;
}

const ROICalculator: React.FC<ROICalculatorProps> = ({ open, onClose }) => {
  const [values, setValues] = useState({
    initialInvestment: "",
    totalRevenue: "",
    totalCosts: "",
    timeframeDays: ""
  });

  const [results, setResults] = useState({
    roi: 0,
    profit: 0,
    roiPercentage: 0,
    dailyROI: 0
  });

  const handleInputChange = (field: string, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    calculateROI({ ...values, [field]: value });
  };

  const calculateROI = (inputs: typeof values) => {
    const investment = parseFloat(inputs.initialInvestment) || 0;
    const revenue = parseFloat(inputs.totalRevenue) || 0;
    const costs = parseFloat(inputs.totalCosts) || 0;
    const days = parseFloat(inputs.timeframeDays) || 1;

    const profit = revenue - (investment + costs);
    const roiAmount = profit;
    const roiPercentage = investment > 0 ? (profit / investment) * 100 : 0;
    const dailyROI = days > 0 ? roiAmount / days : 0;

    setResults({
      roi: roiAmount,
      profit,
      roiPercentage,
      dailyROI
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getROIColor = (percentage: number) => {
    if (percentage > 0) return "text-green-600";
    if (percentage < 0) return "text-red-600";
    return "text-gray-600";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            ROI Calculator
          </DialogTitle>
          <DialogDescription>
            Calculate return on investment for your advertising campaigns and partnerships
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Details</CardTitle>
              <CardDescription>Enter your campaign investment and revenue data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initialInvestment">Initial Investment ($)</Label>
                <Input
                  id="initialInvestment"
                  type="number"
                  placeholder="10000"
                  value={values.initialInvestment}
                  onChange={(e) => handleInputChange('initialInvestment', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="totalRevenue">Total Revenue Generated ($)</Label>
                <Input
                  id="totalRevenue"
                  type="number"
                  placeholder="15000"
                  value={values.totalRevenue}
                  onChange={(e) => handleInputChange('totalRevenue', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="totalCosts">Additional Costs ($)</Label>
                <Input
                  id="totalCosts"
                  type="number"
                  placeholder="2000"
                  value={values.totalCosts}
                  onChange={(e) => handleInputChange('totalCosts', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeframeDays">Campaign Duration (Days)</Label>
                <Input
                  id="timeframeDays"
                  type="number"
                  placeholder="30"
                  value={values.timeframeDays}
                  onChange={(e) => handleInputChange('timeframeDays', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card>
            <CardHeader>
              <CardTitle>ROI Analysis</CardTitle>
              <CardDescription>Your calculated return on investment metrics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold">Total ROI</h4>
                  </div>
                  <p className={`text-2xl font-bold ${getROIColor(results.roiPercentage)}`}>
                    {formatCurrency(results.roi)}
                  </p>
                  <p className="text-sm text-gray-600">Net profit/loss</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">ROI Percentage</h4>
                  </div>
                  <p className={`text-2xl font-bold ${getROIColor(results.roiPercentage)}`}>
                    {results.roiPercentage.toFixed(1)}%
                  </p>
                  <p className="text-sm text-gray-600">Return rate</p>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="h-5 w-5 text-purple-600" />
                    <h4 className="font-semibold">Daily ROI</h4>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(results.dailyROI)}
                  </p>
                  <p className="text-sm text-gray-600">Average per day</p>
                </div>
              </div>

              {/* ROI Interpretation */}
              <div className="mt-6 p-4 rounded-lg bg-blue-50">
                <h4 className="font-semibold text-blue-900 mb-2">ROI Interpretation</h4>
                <div className="text-sm text-blue-800">
                  {results.roiPercentage > 100 && (
                    <p>🎉 Excellent! Your campaign generated over 100% return.</p>
                  )}
                  {results.roiPercentage > 50 && results.roiPercentage <= 100 && (
                    <p>📈 Great performance! Strong positive return on investment.</p>
                  )}
                  {results.roiPercentage > 0 && results.roiPercentage <= 50 && (
                    <p>✅ Positive return, but consider optimizing for better results.</p>
                  )}
                  {results.roiPercentage === 0 && (
                    <p>⚖️ Break-even campaign. No profit or loss.</p>
                  )}
                  {results.roiPercentage < 0 && (
                    <p>⚠️ Negative return. Review campaign strategy and optimization.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => {
            setValues({
              initialInvestment: "",
              totalRevenue: "",
              totalCosts: "",
              timeframeDays: ""
            });
            setResults({
              roi: 0,
              profit: 0,
              roiPercentage: 0,
              dailyROI: 0
            });
          }}>
            Reset Calculator
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ROICalculator;